import { Injectable, inject, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Auth } from 'firebase/auth';
import { firstValueFrom } from 'rxjs';

import { FIREBASE_AUTH } from 'src/app/firebase-config';
import { AuthGuardPolicyService } from '../auth-guard-policy.service';
import { GlobalService } from '../global.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private auth = inject(FIREBASE_AUTH);
  private injector = inject(Injector);
  private router = inject(Router);
  private authGuardPolicy = inject(AuthGuardPolicyService);
  private globalService = inject(GlobalService);

  private async waitForAuthInitialization(): Promise<void> {
    const authWithStateReady = this.auth as Auth & {
      authStateReady?: () => Promise<void>;
    };

    if (typeof authWithStateReady.authStateReady === 'function') {
      await authWithStateReady.authStateReady();
    }
  }

  private isDeletedAuthUserError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const code = 'code' in error ? String((error as { code?: string }).code ?? '') : '';
    return ['auth/user-not-found', 'auth/user-token-expired', 'auth/invalid-user-token'].includes(code);
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    await this.waitForAuthInitialization();

    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return this.router.createUrlTree(['/public/account/login']);
    }

    try {
      await currentUser.reload();
    } catch (error) {
      if (this.isDeletedAuthUserError(error)) {
        return this.router.createUrlTree(['/public/account/login']);
      }
    }

    const maintenanceMode = await firstValueFrom(this.globalService.getMaintenanceMode());
    if (maintenanceMode) {
      return this.router.createUrlTree(['/public/maintenance']);
    }

    try {
      const audience = state.url.startsWith('/worker') ? 'worker' : 'client';
      const decision = await this.authGuardPolicy.resolveAccessDecision(currentUser.uid, audience);

      if (decision === true) {
        return true;
      }

      if (typeof decision === 'string') {
        return this.router.createUrlTree([decision]);
      }

      if (decision instanceof UrlTree) {
        return decision;
      }

      return this.router.createUrlTree(['/public/account/login']);
    } catch (error) {
      console.error('AuthGuard error:', error);
      return this.router.createUrlTree(['/public/account/login']);
    }
  }
}
